import Parse from "parse"
import { goBack } from "react-router-redux"
import dayjs from "dayjs"
import { uniq, uniqBy } from "lodash"
import moment from "moment"
import { onEnter, push, getReplacingPath, haveAccessRight, actionWithLoader } from "../Utils/utils"
import { getSitesWithKitchenAreas } from "../../parseManager/site/parseSiteManager"
import { getTabsBySidebareId, PSE_UPDATED_FROM } from "../../utils/productionStepExecution"

import {
	getIngredientsDoneSelector,
	getIngredientsTodoSelector,
	getProductionStepExecutionSelector,
	getProductionStepsExecutionsSelector,
	getProductionStepsKitchenAreaSelector,
	getProductionStepsProductionDaySelector,
	getProductionStepsSiteSelector,
	getPSEsSidebarNotificationCountsSelector,
	getSelectedPSEsSidebarMenuSelector
} from "../../reducers/Production/ProductionSteps"
import {
	getAllPSEWithCommentsBySection,
	getCountOfPSEsByQuery,
	getFilteredProductionStepExecutions,
	getProductionStepExecution,
	getUlteriorSectionPriorSteps,
	getPSEsFromQuery,
	getPSEsQuery,
	hasPSEByRecipeAndProductionDateExists,
	loadPSEByProductionDateAndSupplierItem,
	ProductionStepExecution,
	saveProductionStepsExecutionsWithHours
} from "../../parseManager/productionStepExecutions/parseProductionStepExecutionManager"
import { openAppMessageSnackBar, showError } from "../Utils/app"
import { computeFirstSubStatus, editableHourToTimestamp, getPSEMachinesWithSameType, mergeUnpackingIngredientsDone, updateUlteriorStepIfAllUnpackingDone, convertBlockIndexToTimestamp, getInsufficientWeight, PSE_SUBSTATUSES, clearMachineBatchFields, PSES_SIDEBAR_MENUS_ENUM, DEFAULT_SELECTED_PSES_SIDEBAR_MENU, DEFAULT_PSES_STATUS_TAB, SIDEBARS_WIDTH_STATUS_TABS } from "../../utils/productionStepExecution"
import { getMachinesByKitchenArea, getWithoutMachine, getMachinesById } from "../../parseManager/machines/machinesManager"
import { KitchenArea } from "../../parseManager/kitchenAreas/kitchenAreasManager"
import { getSecondaryDLCsForProductionStepExecution } from "../../parseManager/secondaryDLC/parseSecondaryDLCManager"
import { groupLotsBySupplierItems } from "../../utils/lotsUtils"
import { COLLECTION_NAMES, convertIdToPointer } from "../../parseUtils"
import { groupSecondaryDLCsBySupplierItems } from "../../utils/secondaryDLCsUtils"
import { getProductionSuppliesFromSupplierItemAndProductionDate } from "../../parseManager/productionSupply/parseProductionSupplyManager"
import { getReusableProductionStep } from "../../parseManager/recipe/parseReusableProductionStepsManager"
import { saveMergeHistory } from "../../parseManager/mergeHistory/mergeHistoryManager"
import { getMachineTypes } from "../../parseManager/machines/machineTypesManager"
import { updateReproductionNeed } from "./Reproduction"
import { parseQueryParamsToUrl } from "../../utils"
import { saveProductionSubstitution } from "../ProductionSubstitutions/ProductionSubstitutions"

const baseUrl = "/productionStepExecutions"

export const PRODUCTION_STEPS_SUPERVISION_URLS = {
	root: baseUrl,
	filter: `${baseUrl}/filter`,
	prodPlannerFilter: `${baseUrl}/prodPlanner/filter`,
	prodPlanner: `${baseUrl}/prodPlanner/view`,
	read: `${baseUrl}/consultation`,
	hoursEdit: `${baseUrl}/hours`,
	preparation: `${baseUrl}/preparation`,
	productionStepExecution: `${baseUrl}/productionStepExecution`,
}

export const PSE_STATUS = {
	locked: "LOCKED",
	todo: "TODO",
	inProgress: "IN_PROGRESS",
	done: "DONE",
	toTest: "TO_TEST",
}


export const generateProductionStepExecutions = ({ productionDate, recipeLabel = null, recipeId = null, isNew = true }) => {
	return actionWithLoader(async (dispatch) => {
		const productionDateFormatted = dayjs(productionDate).format("DD/MM/YYYY")

		// Show "génération lancée" message IMMEDIATELY (before Cloud function call)
		const recipeContext = recipeId !== null && recipeLabel ?
			`La génération des étapes du ${recipeLabel} en production le ${productionDateFormatted} a été lancée...` :
			`La génération des étapes de production pour la journée du ${productionDateFormatted} a été lancée...`

		dispatch(openAppMessageSnackBar(recipeContext, "info", 3000, {}, "LIST"))

		try {
			const user = Parse.User.current() ? Parse.User.current() : null

			const productionDateFormatted = dayjs(productionDate).format("DD/MM/YYYY")
			// Call Parse Cloud function (checks server-side lock + idempotency, then creates Cloud Task)
			const result = await Parse.Cloud.run("createProductionStepExecutionsByProductionItems", { productionDate, recipeId, isNew, userId: user ? user.id : null })

			// Handle different response scenarios
			if (!result.success) {
				// Case 1: Server-side lock - generation already in progress
				if (result.isLocked) {
					dispatch(openAppMessageSnackBar(result.message || "Une génération d'étapes est déjà en cours. Veuillez patienter...", "warning"))
					return
				}
				// Case 2: Idempotency check failed (backend safety net)
				else if (result.existingPSECount !== undefined) {
					dispatch(openAppMessageSnackBar("Les PSEs existent déjà pour cette date.", "warning"))
					return
				}
				// Case 3: Generic error
				else {
					dispatch(openAppMessageSnackBar(result.message || "Une erreur est survenue lors de la generation des étapes de production du " + productionDateFormatted, "error"))
					return
				}
			}

			// Case 4: Cloud Task successfully created - start polling for completion
			pollForPSEGenerationCompletion(dispatch, productionDate, recipeId, productionDateFormatted, recipeLabel)
		} catch (error) {
			dispatch(openAppMessageSnackBar(error.message, "error"))
		}
	})
}

// Helper function to poll for PSE generation completion
const pollForPSEGenerationCompletion = async (dispatch, productionDate, recipeId, productionDateFormatted, recipeLabel) => {
	const maxPolls = 120 // Poll for up to 10 minutes (120 * 5 seconds = 600 seconds)
	let pollCount = 0

	const checkStatus = async () => {
		try {
			pollCount++
			const status = await Parse.Cloud.run("checkPSEGenerationStatus", { productionDate, recipeId })

			if (status.status === "COMPLETED") {
				// Destructure all warnings at top level so variables are accessible throughout the COMPLETED block
				const { zeroWeights, missingUlteriorStep, packagingErrorsList, skippedRecipes, integrityErrors } = status.warnings || {}

				// Check for warnings and log them to console
				if (status.warnings) {
					// Log warnings to console with full details
					if (zeroWeights?.length > 0) console.warn("[PSE Generation] PSEs with zero weights:", zeroWeights)
					if (missingUlteriorStep?.length > 0) console.warn("[PSE Generation] PSEs missing ulteriorStep:", missingUlteriorStep)
					if (packagingErrorsList?.length > 0) console.error("[PSE Generation] Packaging errors:", packagingErrorsList)
					if (skippedRecipes?.length > 0) console.error("[PSE Generation] Skipped recipes (switchProductionSteps not enabled):", skippedRecipes)
					if (integrityErrors?.length > 0) console.error("[PSE Generation] Integrity errors:", integrityErrors)

						// Build warning messages for other warnings
					const warningMessages = []

					if (zeroWeights?.length > 0) {
						const details = zeroWeights.map(w => `${w.uniqueCode} (étape: ${w.stepName} - ${w.pseId})`).filter((v, i, a) => a.indexOf(v) === i).join(", ")
						warningMessages.push(`Les étapes de production ont été générées mais certaines ont un poids théorique à 0. Vérifiez les fiches recette pour: ${details}.`)
					}

					if (missingUlteriorStep?.length > 0) {
						const grouped = new Map()
						missingUlteriorStep.forEach(({ uniqueCode, pseId, stepName }) => {
							if (!grouped.has(uniqueCode)) grouped.set(uniqueCode, [])
							grouped.get(uniqueCode).push({ pseId, stepName })
						})
						const details = Array.from(grouped, ([uniqueCode, pseData]) => 
							`${uniqueCode} (étape: ${pseData.map(p => `${p.stepName} - ${p.pseId}`).join(", ")})`
						).join(", ")
						warningMessages.push(`Les étapes de production ont été générées mais certaines n'ont pas d'étape ultérieure. Vérifiez les fiches recette pour: ${details}.`)
					}

					if (packagingErrorsList?.length > 0) {
						const grouped = new Map()
						packagingErrorsList.forEach(({ uniqueCode, pseId, stepName }) => {
							if (!grouped.has(uniqueCode)) grouped.set(uniqueCode, [])
							if (pseId) grouped.get(uniqueCode).push({ pseId, stepName })
						})
						const uniqueCodes = [...grouped.keys()].filter(Boolean)
						const recetteLabel = uniqueCodes.length > 1 ? "les recettes" : "la recette"
						warningMessages.push(`Les étapes de production ont été générées, mais une erreur de configuration du packaging a été détectée pour ${recetteLabel} : ${uniqueCodes.join(", ")}.`)
					}

					// Show warning snackbar if there are other warnings
					if (warningMessages.length > 0) {
						warningMessages.forEach((message) => {
							dispatch(openAppMessageSnackBar(message, "warning", null, { disableClickAway: false }, "LIST"))
						})
					}
				}

				// Check if generation had partial failures
				if (status.hasWarnings && status.failedRecipesCount > 0) {
					// Partial success - some recipes failed
					const failedRecipesDisplay = status.failedRecipeUniqueCodes?.length > 0 ? status.failedRecipeUniqueCodes.join(", ") : `${status.failedRecipesCount} recette(s)`
					const warningMessage = `Génération terminée avec des erreurs pour: ${failedRecipesDisplay}. Les étapes des autres recettes ont été créées.`
					dispatch({ type: "CLEAR_SUCCESS_MESSAGES" })
					dispatch(openAppMessageSnackBar(warningMessage, "warning", null, { disableClickAway: true }, "LIST"))
				}

				// Everything is done server-side - show success if no warnings
				if (!status.warnings || Object.values(status.warnings).every(arr => arr?.length === 0)) {
					const successMessage = recipeId !== null && recipeLabel ?
						`Les étapes du ${recipeLabel} pour le ${productionDateFormatted} ont bien été générées.` :
						`Les étapes de production pour la journée du ${productionDateFormatted} ont bien été générées.`
					dispatch({ type: "CLEAR_MESSAGES" }) // Clear any previous messages to avoid stacking with the success message
					dispatch(openAppMessageSnackBar(successMessage, "success", null, { disableClickAway: true }, "LIST"))
				}

				// Skipped recipes - shown LAST so it is not overwritten by success/warning snackbars
				// (all snackbars share the same Redux slot, last dispatch wins)
				if (skippedRecipes?.length > 0) {
					const codes = skippedRecipes
						.map(r => r.uniqueCode || r.recipeName)
						.filter(Boolean)
						.join(", ")
						
					dispatch(openAppMessageSnackBar(
						`Les recettes suivantes n'ont pas d'étapes de production générées car elles ne sont pas encore basculées en nouvelle fiche digitale. Sans actions correctives, ces recettes ne pourront pas être produites : ${codes}.`,
						"error",
						null,
						{ disableClickAway: true },
						"LIST"
					))
				}

				// Integrity errors - shown after everything else: the most serious message must stay visible
				if (integrityErrors?.length > 0) {
					const codes = [...new Set(integrityErrors.flatMap(error => (error.uniqueCode || "").split(", ")).filter(Boolean))]
					const recipesLabel = codes.length > 1 ? "les recettes" : "la recette"
					// a single recipe generation (regeneration or added recipe, which has no label) must not suggest relaunching the whole day
					const relaunchMessage = recipeId !== null ?
						`Relancez la génération des étapes du ${recipeLabel || codes.join(", ")}.` :
						`Relancez la génération des étapes pour la journée du ${productionDateFormatted}.`

					dispatch(openAppMessageSnackBar(
						`Les étapes de production ont été générées mais un problème d'intégrité a été détecté pour ${recipesLabel} : ${codes.join(", ")}. ${relaunchMessage}`,
						"error",
						null,
						{ disableClickAway: true },
						"LIST"
					))
				}
				return // Stop polling - everything is done
			} else if (status.status === "ERROR") {
				// Fatal error during generation
				let errorMessage = status.message
				if (status.fileName && status.lineNumber && status.fileName !== "unknown") {
					errorMessage += ` (${status.fileName}:${status.lineNumber})`
				}

				// Check if it's a recipe-specific error
				if (status.isRecipeError && status.errorRecipeUniqueCode) {
					dispatch(openAppMessageSnackBar(
						`Une erreur est survenue sur la recette ${status.errorRecipeUniqueCode}. Veuillez contrôler la fiche recette et réessayer.`,
						"error",
						null,
						{ disableClickAway: true }
					))
				} else {
					dispatch(openAppMessageSnackBar(
						`Erreur lors de la génération des PSE pour le ${productionDateFormatted}: ${errorMessage}`,
						"error"
					))
				}
				return // Stop polling
			} else if (status.status === "STALE") {
				// Timeout - generation took too long
				dispatch(openAppMessageSnackBar(`La génération des PSE pour le ${productionDateFormatted} a dépassé le délai maximum de 60 minutes. Veuillez vérifier dans la vue Flow que les étapes ont été créées.`, "error", null, { disableClickAway: true }))
				return // Stop polling
			} else if (status.status === "IN_PROGRESS" && pollCount < maxPolls) {
				// Show message based on current phase
				const phase = status.phase || "PSE_CREATION"
				if (phase === "DELETING_OLD_PSES") {
					dispatch(openAppMessageSnackBar("Suppression des étapes précédemment générées...", "info", 3000, {}, "LIST"))
				} else if (phase === "MERGING") {
					dispatch(openAppMessageSnackBar("Mutualisation des étapes de production en cours...", "info", 3000, {}, "LIST"))
				} else if (phase === "PRODUCTION_NEEDS") {
					dispatch(openAppMessageSnackBar("Mise à jour des besoins de production en cours...", "info", 3000, {}, "LIST"))
				} else if (phase==="PSE_CREATION"){
					dispatch(openAppMessageSnackBar("Création des étapes de production en cours...", "info", 3000, {}, "LIST"))
				}
				else {
					dispatch(openAppMessageSnackBar("Processus en cours...", "info", 3000, {}, "LIST"))
				}
				setTimeout(checkStatus, 5000)
			} else if (status.status === "UNKNOWN") {
				// No lock found
				return
			} else if (pollCount >= maxPolls) {
				// Max polls reached, stop but don't show error (generation might still succeed)
				console.warn(`[PSE Poll ${pollCount}] Max polls reached - Stopping (generation might still succeed)`)
				return
			}
		} catch (error) {
			console.error(`[PSE Poll ${pollCount}] Error polling status:`, error)
			// Don't show error to user - generation might still succeed
		}
	}
	// Start polling after 3 seconds (give the Cloud Task time to start)
	setTimeout(checkStatus, 3000)
}

export const generatePSEsForANewAddedRecipe = ({ productionDate, recipeId }) => {
	return actionWithLoader(async (dispatch) => {
		const hasPSE = await hasPSEByRecipeAndProductionDateExists(recipeId, productionDate)
		// disallow PSE generation if it already exists
		if (hasPSE) return
		// isNew = true because it will create new PSEs
		dispatch(generateProductionStepExecutions({ productionDate, recipeId, isNew: true }))
	})
}

export const doPSEsExist = async (productionDate) => {
	const response = await Parse.Cloud.run("doPSEsExist", { productionDate })

	return response?.success ? response.exists : null
}

export const loadProductionStepsSupervisionFilters = () => {
	return actionWithLoader(async (dispatch) => {
		const localStorage = window.localStorage
		const selectedSiteFromStorage = localStorage.getItem("selectedProductionStepsSite")
		const selectedKitchenAreaFromStorage = localStorage.getItem("selectedProductionStepsKitchenArea")
		const selectedProductionDayFromStorage = localStorage.getItem("selectedProductionStepsProductionDay")
		const sites = await getSitesWithKitchenAreas()

		const selectedSite = selectedSiteFromStorage ? JSON.parse(selectedSiteFromStorage) : sites[0]
		const selectedKitchenArea = selectedKitchenAreaFromStorage ? JSON.parse(selectedKitchenAreaFromStorage) : selectedSite.kitchenAreas.length ? selectedSite.kitchenAreas[0] : null
		const selectedProductionDay = selectedProductionDayFromStorage ? selectedProductionDayFromStorage : moment.utc().format("YYYY-MM-DD")

		dispatch({
			type: "FILTERS_LOADED_FOR_PRODUCTION_STEPS",
			selectedSite,
			selectedKitchenArea,
			selectedProductionDay
		})

		dispatch({
			type: "SITES_LOADED_FOR_PRODUCTION_STEPS",
			sites
		})
	})
}


export const updateProductionStepsFilters = (site, kitchenArea, productionDay) => {
	return async dispatch => {
		const localStorage = window.localStorage
		if (site) {
			localStorage.setItem("selectedProductionStepsSite", JSON.stringify(site))
		}
		if (kitchenArea) {
			localStorage.setItem("selectedProductionStepsKitchenArea", JSON.stringify(kitchenArea))
		}
		const formattedProductionDay = moment.utc(productionDay).format("YYYY-MM-DD")
		if (formattedProductionDay) {
			localStorage.setItem("selectedProductionStepsProductionDay", formattedProductionDay)
		}
		dispatch({
			type: "FILTERS_LOADED_FOR_PRODUCTION_STEPS",
			selectedSite: site,
			selectedKitchenArea: kitchenArea,
			selectedProductionDay: formattedProductionDay
		})
	}
}

export const loginProductionStepsSupervision = () => {
	return async (dispatch) => {
		dispatch(showProductionStepsSupervision())
	}
}

export const loadSelectedSideBarMenu = (selectedSidebar) => {
	return async (dispatch) => {
		dispatch({
			type: "LOAD_SELECTED_SIDEBAR",
			selectedSidebar
		})
	}
}

export const clearSelectedSideBarMenu = () => {
	return async (dispatch) => {
		dispatch({
			type: "CLEAR_SELECTED_SIDEBAR"
		})
	}
}

export const loadAllProductionStepExecutionsForListPage = () => {
	// No loader here because each thunk it calls has its own loader
	return async (dispatch) => {
		dispatch({ type: "CLEAR_NOTIFICATIONS_COUNT" })
		/**
		 * The default sidebar tab is normal PSEs
		 * So we need to load it with a loader
		 */
		await dispatch(loadProductionStepsExecutions())

		// --------- background query --------- //
		/**
		 * We also preload the unpacking PSEs without loader
		 * Mainly to have the notifications count updated in the sidebar
		 */
		await dispatch(loadUnpackingPSEsBySupplierItems(false))

		await dispatch(loadPSEsNotificationsCounts())
	}
}

/**
 * Called when the page is loaded or when the sidebar menu or status tab is changed
 * 1. Loads the production step executions filtered by sidebar menu and status tab
 * 2. Updates the notification counts for the selected sidebar menu and status tab
 */
export const loadProductionStepsExecutions = (selectedSidebarId = null, selectedStatus = null) => {
	return actionWithLoader(async (dispatch, getState) => {
		const state = getState()
		const storedSelectedSidebar = getSelectedPSEsSidebarMenuSelector(state)
		const productionDay = getProductionStepsProductionDaySelector(state)

		const kitchenArea = getProductionStepsKitchenAreaSelector(state)
		const prevNotificationCounts = getPSEsSidebarNotificationCountsSelector(state)

		if (selectedSidebarId && selectedSidebarId !== storedSelectedSidebar) {
			await dispatch(loadSelectedSideBarMenu(selectedSidebarId))
		} else if (storedSelectedSidebar) {
			selectedSidebarId = storedSelectedSidebar
		}

		// Get PSEs
		const filters = { productionDay, kitchenArea }

		const query = getPSEsQuery({
			filters,
			fieldsToInclude: ["priorSteps.step", "productionItems.brand"],
			selectedSidebarId,
			selectedStatus
		})

		const productionStepsExecutions = await getPSEsFromQuery(query)

		dispatch({
			type: "PRODUCTION_STEPS_EXECUTIONS_LOADED",
			productionStepsExecutions
		})

		// Update notification counts
		const key = selectedSidebarId || DEFAULT_SELECTED_PSES_SIDEBAR_MENU
		const statusKey = selectedStatus || DEFAULT_PSES_STATUS_TAB
		const count = productionStepsExecutions.length

		// const isTastingSidebarSelected = selectedSidebarId && selectedSidebarId === PSES_SIDEBAR_MENUS_ENUM.TASTING
		let total = count

		// When a sidebar menu is selected, subtract the previous count, then add the new count (it may have changed)
		total = prevNotificationCounts[key].total - prevNotificationCounts[key][statusKey] + count

		const notificationCounts = {
			...prevNotificationCounts,
			[key]: {
				...prevNotificationCounts[key],
				[statusKey]: count,
				total: total
			}
		}

		dispatch({
			type: "LOAD_NOTIFICATIONS_COUNT",
			notificationCounts
		})
	})
}

/**
 * Load notification count for PSE sidebar menus and status tabs
 * The default selected Sidebar (cooking) is already loaded with loadProductionStepsExecution
 * We set the remaining normal sidebars (tasting and weighing) counts here
 * So we load it in the background without blocking the UI (no loader)
 */
export const loadPSEsNotificationsCounts = () => {
	return async (dispatch, getState) => {
		const state = getState()
		const productionDay = getProductionStepsProductionDaySelector(state)
		const kitchenArea = getProductionStepsKitchenAreaSelector(state)
		const notificationCounts = getPSEsSidebarNotificationCountsSelector(state)
		const productionStepExecutions = getProductionStepsExecutionsSelector(state)
		const storedSelectedSidebar = getSelectedPSEsSidebarMenuSelector(state)

		const filters = { productionDay, kitchenArea }

		const firstPSE = productionStepExecutions[0]
		const pseStatus = firstPSE ? firstPSE.status : null

		// cooking, tasting, weighing
		for (const sidebarId of Object.values(PSES_SIDEBAR_MENUS_ENUM)) {
			if (sidebarId !== PSES_SIDEBAR_MENUS_ENUM.UNPACKING) {
				// cooking and weighing
				if (SIDEBARS_WIDTH_STATUS_TABS.includes(sidebarId)) {
					// todo, locked, in_progress, done
					for (const status of getTabsBySidebareId(sidebarId)) {
						// other than current status (tab) and sidebar, we need to fetch the count
						const shouldFetch = pseStatus !== status || (storedSelectedSidebar !== sidebarId && pseStatus === status)

						if (shouldFetch) {
							const query = getPSEsQuery({ filters, selectedSidebarId: sidebarId, selectedStatus: status })
							const count = await getCountOfPSEsByQuery(query)
							notificationCounts[sidebarId][status] = count
							notificationCounts[sidebarId].total = Object.keys(notificationCounts[sidebarId]).filter(key => key !== "total").reduce((acc, key) => acc + (notificationCounts[sidebarId][key] || 0), 0)
						}
					}
					// tasting (without the notion of status tabs)
				} else {
					const query = getPSEsQuery({ filters, selectedSidebarId: sidebarId })
					const count = await getCountOfPSEsByQuery(query)
					notificationCounts[sidebarId].total = count
				}
			}
		}

		dispatch({
			type: "LOAD_NOTIFICATIONS_COUNT",
			notificationCounts: notificationCounts
		})
	}
}

/**
 * Load production step executions for the ProdPlanner view
 * Fetches PSEs filtered by kitchen area and production day with specific fields
 * needed for planning and scheduling operations
 */
export const loadProdPlannerData = () => {
	return actionWithLoader(async (dispatch, getState) => {
		const state = getState()
		const productionDay = getProductionStepsProductionDaySelector(state)
		const kitchenArea = getProductionStepsKitchenAreaSelector(state)

		// Build filters using current production day and kitchen area from state
		const filters = { productionDay, kitchenArea }

		// Retrieve PSEs with only necessary fields for ProdPlanner
		// Note: productionStepSnapshot includes nested stepComponents for ingredient traversal
		const productionStepsExecutions = await getFilteredProductionStepExecutions({
			filters,
			fieldsToSelect: [
				"objectId",
				"uniqueCode",
				"recipeName",
				"sectionName",
				"productionStepSnapshot",         // Required for ingredients and recipe details
				"productionDate",
				"stepProductionDate",
				"productionItems.packagingDate",  // For barquettage date display
				"productionItems.brand",
				"theoreticalGrossWeight",
				"theoreticalNetWeight",
				"status",                         // PSE execution status
				"theoreticalStartTime",           // Planning time fields
				"theoreticalEndTime",
				"startTime",                      // Actual execution time fields
				"endTime",
				"isStepMerged",                   // For merged PSE visual indication
				"original",                       // Reference to original PSE if merged
				"machinesBatch",
				"ulteriorStep.productionStepSnapshot.transformation",
				"mergedProductionStepExecutions.uniqueCode",
				"mergedProductionStepExecutions.sectionName",
			]
		})

		dispatch({
			type: "PRODUCTION_STEPS_EXECUTIONS_LOADED",
			productionStepsExecutions
		})
	})
}

export const loadUnpackingPSEsBySupplierItems = (withLoader = true) => {
	const thunk = async (dispatch, getState) => {
		// update the filter so it can be display in page header when refreshing
		await dispatch(loadProductionStepsSupervisionFilters())

		const state = getState()
		const localStorage = window.localStorage

		const selectedKitchenAreaFromStorage = localStorage.getItem("selectedProductionStepsKitchenArea")
		const selectedProductionDayFromStorage = localStorage.getItem("selectedProductionStepsProductionDay")
		let productionDay = getProductionStepsProductionDaySelector(state)
		let kitchenArea = getProductionStepsKitchenAreaSelector(state)
		const notificationCounts = getPSEsSidebarNotificationCountsSelector(state)

		if (!productionDay) {
			productionDay = selectedProductionDayFromStorage
		}

		if (!kitchenArea) {
			kitchenArea = selectedKitchenAreaFromStorage ? JSON.parse(selectedKitchenAreaFromStorage) : null
		}

		const { ingredientsTodo, ingredientsDone } = await Parse.Cloud.run("getUnpackingSupplierItems", {
			productionDate: productionDay,
			kitchenAreaId: kitchenArea?.objectId
		})

		dispatch({
			type: "UNPACKING_PSES_BY_SUPPLIER_ITEMS_LOADED",
			ingredientsTodo,
			ingredientsDone
		})

		const totalUnpacking = ingredientsTodo.length + ingredientsDone.length

		dispatch({
			type: "LOAD_NOTIFICATIONS_COUNT",
			notificationCounts: {
				...notificationCounts,
				[PSES_SIDEBAR_MENUS_ENUM.UNPACKING]: { total: totalUnpacking }
			}
		})
	}

	if (withLoader) {
		return actionWithLoader(thunk)
	}
	return thunk
}

export const changeUnpackingIngredientsStatus = (ingredient, currentStatus = "TODO") => {
	return actionWithLoader(async (dispatch, getState) => {
		const state = getState()
		const ingredientsTodo = getIngredientsTodoSelector(state)
		const ingredientsDone = getIngredientsDoneSelector(state)

		const updatedIngredientsTodoIds = []
		const updatedIngredientsDoneIds = []

		await new Parse.Query(ProductionStepExecution)
			.containedIn("objectId", ingredient.productionStepExecutions.map((pse) => pse.objectId))
			.include([
				"unpackingProgress.supplierItem",
				"ulteriorStep",
				"ulteriorStep.priorSteps.step",
				"mergedProductionStepExecutions.productionStepExecution",
				"mergedProductionStepExecutions.productionStepExecution.ulteriorStep",
				"mergedProductionStepExecutions.productionStepExecution.ulteriorStep.priorSteps.step",
				"section"
			])
			.each(async productionStepExecution => {
				const unpackingProgress = productionStepExecution.get("unpackingProgress") || []
				const newUnpackingProgress = unpackingProgress.map(unpacking => {
					if (unpacking.supplierItem.id === ingredient.objectId) {
						return { ...unpacking, status: currentStatus === "TODO" ? "DONE" : "TODO" }
					}
					return unpacking
				})

				productionStepExecution.set("unpackingProgress", newUnpackingProgress)

				// Compute the number of unpacked ingredients, the total of ingredients inside the PSE,
				// the PSE status and update it if needed.
				const unpackingDoneCount = newUnpackingProgress.filter(pse => pse.status === "DONE").length
				const unpackingTotalCount = newUnpackingProgress.length
				const previousPSEStatus = productionStepExecution.get("status")

				// Set the PSE status to DONE if all ingredients are unpacked
				if (unpackingDoneCount > 0 && previousPSEStatus === "TODO") productionStepExecution.set("status", "IN_PROGRESS")
				// Set the PSE status to IN_PROGRESS if there is at least one ingredient unpacked
				if (unpackingDoneCount === unpackingTotalCount && unpackingTotalCount > 0) productionStepExecution.set("status", "DONE")

				// Single save after all local mutations to ensure afterSave sees the correct transition
				const updatedProductionStepExecution = await productionStepExecution.save(null, global.USE_MASTER_KEY)

				// Only propagate when the step is DONE, avoiding partial propagation mid-progress
				if (updatedProductionStepExecution.get("status") === "DONE") {
					const ulteriorStep = updatedProductionStepExecution.get("ulteriorStep")
					if (ulteriorStep && await isPSECanBeUnlocked(ulteriorStep)) await updateUlteriorStepIfAllUnpackingDone(ulteriorStep)

					const mergedSteps = updatedProductionStepExecution.get("mergedProductionStepExecutions") || []
					
					// Update each merged PSE's ulterior step
					for (const { productionStepExecution: mergedPSE } of mergedSteps) {
						mergedPSE.set("status", "DONE")
						mergedPSE.set("unpackingProgress", newUnpackingProgress)
						await mergedPSE.save(null, global.USE_MASTER_KEY)
						const mergedUlteriorStep = mergedPSE.get("ulteriorStep")
						if (mergedUlteriorStep) await updateUlteriorStepIfAllUnpackingDone(mergedUlteriorStep)
					}

					// Also update the main PSE's ulterior step (the one containing the merged steps)
					const mainUlteriorStep = updatedProductionStepExecution.get("ulteriorStep")
					if (mainUlteriorStep) await updateUlteriorStepIfAllUnpackingDone(mainUlteriorStep)
				}

				if (currentStatus === "TODO") {
					updatedIngredientsTodoIds.push(updatedProductionStepExecution.id)
				} else if (currentStatus === "DONE") {
					updatedIngredientsDoneIds.push(updatedProductionStepExecution.id)
				}
			})

		if (currentStatus === "TODO") {
			// remove the ingredient from the to list and move it to the done list
			const newIngredientsTodo = ingredientsTodo.filter((ingredientTodo) => {
				const isTheIngredientUpdated = updatedIngredientsTodoIds.includes(ingredientTodo.objectId)
				return !isTheIngredientUpdated && (ingredientTodo.objectId !== ingredient.objectId)
			})
			const newIngredientsDone = [ingredient, ...ingredientsDone]

			const mergedIngredients = mergeUnpackingIngredientsDone(newIngredientsDone)

			dispatch({
				type: "UNPACKING_PSES_BY_SUPPLIER_ITEMS_LOADED",
				ingredientsTodo: newIngredientsTodo,
				ingredientsDone: mergedIngredients
			})
			// for now it's not used
		} else if (currentStatus === "DONE") {
			// remove the ingredient from the done list and move it to the to list
			const newIngredientsDone = ingredientsDone.filter((ingredientDone) => {
				const isTheIngredientUpdated = updatedIngredientsDoneIds.includes(ingredientDone.objectId)
				return isTheIngredientUpdated && (ingredientDone.objectId !== ingredient.objectId)
			})
			const newIngredientsTodo = [ingredient, ...ingredientsTodo]

			dispatch({
				type: "UNPACKING_PSES_BY_SUPPLIER_ITEMS_LOADED",
				ingredientsTodo: newIngredientsTodo,
				ingredientsDone: newIngredientsDone
			})
		}
	})
}

/**
 * 
 * @param {Array} supplierItems // original supplier items from pse
 * @param {Array} substitutesSupplierItems // substitutes supplier items from pse (from REMPLACEMENT DANS UNE PRODUCTION)
 * @param {String} kitchenAreaId // kitchen area id
 * @param {String} productionDate // production date
 * @returns 
 */

export const loadPSELotsAndSecondaryDLCsSelection = ({ supplierItems, substitutesSupplierItems = [], kitchenAreaId, productionDate }) => {
	return async dispatch => {
		const supplierItemsIds = supplierItems.map((supplierItem) => {
			const currentSupplierItem = substitutesSupplierItems.find(substitute => substitute.originalSupplierItem.objectId === supplierItem.objectId)
			const replacementSupplierItem = currentSupplierItem?.replacementSupplierItem
			if (replacementSupplierItem) {
				return replacementSupplierItem.objectId
			}

			return supplierItem.objectId
		})

		const [productionSupplies, productionSuppliesReplacement] = await Promise.all([
			getProductionSuppliesFromSupplierItemAndProductionDate({
				productionDate,
				supplierItemsIds,
				toJSON: false
			}),
			getProductionSuppliesFromSupplierItemAndProductionDate({
				productionDate,
				supplierItemsIds,
				replacement: true,
				toJSON: false
			})])

		const allProductionSupplies = productionSupplies.concat(productionSuppliesReplacement)

		const lots = allProductionSupplies.flatMap(productionSupply => productionSupply.get("lots")).map(lot => lot.toJSON())

		const lotsBySupplierItems = groupLotsBySupplierItems(lots, supplierItemsIds, productionDate)

		const secondaryDLCs = await getSecondaryDLCsForProductionStepExecution({
			kitchenAreaId,
			supplierItemsIds,
			productionSuppliesIds: uniq(allProductionSupplies.concat(productionSuppliesReplacement).map(productionSupply => productionSupply.id))
		})

		const secondaryDLCsBySupplierItems = groupSecondaryDLCsBySupplierItems(secondaryDLCs, supplierItemsIds)


		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION_LOTS_SECONDARYDLCS",
			lotsBySupplierItems,
			secondaryDLCsBySupplierItems
		})
	}
}

export const loadProductionStepExecution = (params) => {
	return actionWithLoader(async (dispatch) => {
		// for the header
		const selectedSiteFromStorage = localStorage.getItem("selectedProductionStepsSite")
		const selectedKitchenAreaFromStorage = localStorage.getItem("selectedProductionStepsKitchenArea")
		const selectedProductionDayFromStorage = localStorage.getItem("selectedProductionStepsProductionDay")

		dispatch({
			type: "FILTERS_LOADED_FOR_PRODUCTION_STEPS",
			selectedSite: JSON.parse(selectedSiteFromStorage),
			selectedKitchenArea: JSON.parse(selectedKitchenAreaFromStorage),
			selectedProductionDay: selectedProductionDayFromStorage
		})

	const PSEFieldsToInclude = [
		"productionStepSnapshot.stepComponents.supplierItem",
		"substitutes.replacementSupplierItem",
		"priorSteps.step",
		"productionStepSnapshot.machineType",
		"productionStepSnapshot.kitchenArea",
		"productionStepSnapshot.machineSetting",
		"priorSteps.step.unpackingProgress.supplierItem",
		"priorSteps.substitutes.replacementSupplierItem",
		"priorSteps.substitutes.originalSupplierItem",
		"machinesBatch",
		"machinesBatch.machine",
		"ulteriorStep",
		"section",
		"recipe.dlc",
		"sectionOutputWeights",
		"isSectionLastStep",
		"packagingExecutions",
		"packagingExecutions.productionItems",
	]
	
	const productionStepExecution = await getProductionStepExecution(params.id, true, PSEFieldsToInclude)
		/** KFC-2414 Hide lots and secondary DLC loading temporarily **/
		// const productionStepExecutionsSupplierItems = productionStepExecution.productionStepSnapshot.stepComponents.map(stepComponent => stepComponent.supplierItem).filter(supplierItem => !!supplierItem)
		// if (
		// 	productionStepExecutionsSupplierItems.length
		// 	&& (productionStepExecution.status === PSE_STATUS.inProgress || productionStepExecution.status === PSE_STATUS.todo)
		// ) {
		// 	await loadPSELotsAndSecondaryDLCsSelection({
		// 		supplierItems: productionStepExecutionsSupplierItems,
		// 		substitutesSupplierItems: productionStepExecution.substitutes,
		// 		kitchenAreaId: JSON.parse(selectedKitchenAreaFromStorage).objectId,
		// 		productionDate: productionStepExecution.stepProductionDate
		// 	})(dispatch)
		// }

		let allPSEComments = []
		let section = null

		if (productionStepExecution.section) {
			// Load section with sectionOutputs for isSectionLastStep PSEs, and also for the
			// last non-TECHNICAL step whose ulteriorStep is a TECHNICAL isSectionLastStep —
			// that step acts as the proxy for the weight distribution screen.
			const ulteriorStep = productionStepExecution.ulteriorStep
			const isLastBeforeTechnicalSectionLastStep = (
				ulteriorStep?.isSectionLastStep
				&& ulteriorStep?.productionStepSnapshot?.transformation === "TECHNICAL"
			)
			if (productionStepExecution.isSectionLastStep || isLastBeforeTechnicalSectionLastStep) {
				({ section, comments: allPSEComments } = await getAllPSEWithCommentsBySection(productionStepExecution.section.objectId, productionStepExecution.productionDate, { withSection: true }))
			} else {
				allPSEComments = await getAllPSEWithCommentsBySection(productionStepExecution.section.objectId, productionStepExecution.productionDate)
			}
		}

		// if there is no machine in the batch, we add the without machine
		if (!productionStepExecution.machinesBatch || productionStepExecution.machinesBatch.length === 0) {
			const withoutMachine = await getWithoutMachine()
			productionStepExecution.machinesBatch = [{
				machine: withoutMachine,
				machineName: withoutMachine.name
			}]
		}

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution,
			allPSEComments,
			section: section
		})
	})
}

const isPSECanBeUnlocked = async (step) => {
	// check if the step is not a section output step
	let isPriorStepPass = false

	if (!step.has("priorSteps")) isPriorStepPass = true
	else {
		const priorSteps = step.get("priorSteps") || []

		const allStatuses = []
		for (const priorStep of priorSteps) {
			if (priorStep.step.get("isMergeBasis") && !priorStep.step.get("isActive")) {
				const mergedPriorStep = await new Parse.Query(ProductionStepExecution)
					.equalTo("mergedProductionStepExecutions.productionStepExecution", convertIdToPointer("ProductionStepExecutions", priorStep.step.id))
					.first()
				mergedPriorStep && allStatuses.push(mergedPriorStep.get("status"))
			} else {
				allStatuses.push(priorStep.step.get("status"))
			}
		}

		// set the ulteriorStep as TODO if all its priorSteps are DONE
		isPriorStepPass = allStatuses.every(status => status === PSE_STATUS.done)

	}
	if (!isPriorStepPass) return false

	// --- sectionOutput check ---
	const snapshot = step.get("productionStepSnapshot") || {}
	const stepComponents = snapshot.stepComponents || []

	// outputPSE stored directly inside each sectionOutput in the snapshot
	const outputPSEIds = stepComponents
		.map(sc => sc?.sectionOutput?.outputPSE?.objectId || sc?.sectionOutput?.outputPSE?.id)
		.filter(Boolean)

	if (outputPSEIds.length > 0) {
		const producerPSEs = await new Parse.Query(ProductionStepExecution)
			.containedIn("objectId", outputPSEIds)
			.find()
		const allProducersDone = producerPSEs.length > 0 && producerPSEs.every(p => p.get("status") === PSE_STATUS.done)
		return allProducersDone
	}

	// No sectionOutput dependency — prior steps check was already enough
	return true
}


export const updateProductionStepExecutionStatusToDone = (id, values) => {
	return actionWithLoader(async (dispatch) => {
		const toBeIncluded = [
			"ulteriorStep",
			"section",
			"mergedProductionStepExecutions",
			"mergedProductionStepExecutions.productionStepExecution",
			"mergedProductionStepExecutions.ulteriorStep",
		]
		const productionStepExecution = await getProductionStepExecution(id, false, toBeIncluded)
		// --------- current pse --------- //
		// Capture the current status BEFORE mutating, to detect toTest → done transition
		const previousStatus = productionStepExecution.get("status")

		productionStepExecution.set("endTime", dayjs.utc().valueOf())
		productionStepExecution.set("status", values.status)
		if (productionStepExecution.has("subStatus")) {
			productionStepExecution.unset("subStatus")
		}

		// --- merged pse --- //
		const individualPSEs = productionStepExecution.get("mergedProductionStepExecutions") || []

		// keep track of temporarily activated PSE to deactivate them later: usefull for unlocking ulterior steps
		const temporaryActivePSE = []

		// Only process individual merged PSEs when transitioning from inProgress → done/toTest.
		// When transitioning toTest → done, the individual PSEs were already set correctly;
		// re-processing them would wrongly push isSectionLastStep ones back to toTest.
		const isToTestToDone = previousStatus === PSE_STATUS.toTest && values.status === PSE_STATUS.done

		if (individualPSEs.length > 0 && !isToTestToDone) {

			// if all mergedPSE are last step of their section, we set the current pse as inactive
			if (individualPSEs.every(({ productionStepExecution: individualPSE }) => individualPSE.get("isSectionLastStep"))) {
				productionStepExecution.set("isActive", false)
			}

			for (const { productionStepExecution: individualPSE } of individualPSEs) {
				if (individualPSE.get("isSectionLastStep")) {
					individualPSE.set("status", PSE_STATUS.toTest)
				} else {
					individualPSE.set("status", PSE_STATUS.done)
					temporaryActivePSE.push(individualPSE)
				}
				individualPSE.set("isActive", true)
				individualPSE.set("startTime", productionStepExecution.get("startTime"))
				individualPSE.set("endTime", productionStepExecution.get("startTime"))
				individualPSE.set("machinesBatch", productionStepExecution.get("machinesBatch"))

				if (individualPSE.has("subStatus")) {
					individualPSE.unset("subStatus")
				}

				await individualPSE.save(null, { context: { from: PSE_UPDATED_FROM.CLIENT_INDIVIDUAL_STATUS_CHANGE } })
			}
		} else if (individualPSEs.length > 0 && isToTestToDone) {
			// toTest → done: set all individual merged PSEs to done and unlock their ulterior steps / section outputs
			for (const mergedEntry of individualPSEs) {
				const mergedPSE = mergedEntry.productionStepExecution
				if (!mergedPSE) continue

				mergedPSE.set("status", PSE_STATUS.done)
				await mergedPSE.save()
				const mergedPSEJson = mergedPSE.toJSON()

				// Unlock ulterior step of the individual PSE
				const ulteriorStepRef = mergedPSE.get("ulteriorStep") || mergedEntry.ulteriorStep
				if (ulteriorStepRef) {
					const ulteriorStepId = ulteriorStepRef.id || ulteriorStepRef.objectId
					const ulteriorStep = await getProductionStepExecution(ulteriorStepId, false, ["priorSteps.step"])
					if (ulteriorStep && ulteriorStep.get("status") === PSE_STATUS.locked && await isPSECanBeUnlocked(ulteriorStep)) {
						ulteriorStep.set("status", PSE_STATUS.todo)
						await ulteriorStep.save()
					}
				}

				// Unlock ulterior section prior steps if individual PSE is isSectionLastStep
				if (mergedPSEJson.isSectionLastStep && mergedPSEJson.section?.objectId) {
					const sectionWithOutputs = await new Parse.Query(Parse.Object.extend("Section"))
						.equalTo("objectId", mergedPSEJson.section.objectId)
						.first()
					const sectionOutputs = sectionWithOutputs?.get("sectionOutputs") || []
					if (sectionOutputs.length > 0) {
						const ulteriorSectionPriorSteps = await getUlteriorSectionPriorSteps(
							sectionOutputs,
							mergedPSEJson.stepProductionDate || mergedPSEJson.productionDate
						)
						for (const ulteriorSectionPriorStep of ulteriorSectionPriorSteps) {
							if (ulteriorSectionPriorStep.get("status") === PSE_STATUS.locked && await isPSECanBeUnlocked(ulteriorSectionPriorStep)) {
								ulteriorSectionPriorStep.set("status", PSE_STATUS.todo)
								await ulteriorSectionPriorStep.save()
							}
						}
					}
				}
			}
		}

		const newProductionStepExecution = await productionStepExecution.save()
		const productionStepExecutionJson = newProductionStepExecution.toJSON()


		//if last step, check if there is a section output
		const sectionsToCheck = []
		if (productionStepExecutionJson.isSectionLastStep && productionStepExecutionJson.section?.objectId) {
			// regular PSE: fetch section with sectionOutputs
			const sectionWithOutputs = await new Parse.Query(Parse.Object.extend("Section"))
				.equalTo("objectId", productionStepExecutionJson.section.objectId)
				.first()
			if (sectionWithOutputs?.get("sectionOutputs")?.length > 0) {
				sectionsToCheck.push({ objectId: productionStepExecutionJson.section.objectId, sectionOutputs: sectionWithOutputs.get("sectionOutputs") })
			}
		} else if (productionStepExecutionJson.mergedProductionStepExecutions?.length > 0) {
			// reusable (merged) PSE: collect section IDs from each merged PSE that is isSectionLastStep
			const sectionIdsToFetch = new Set()
			for (const merged of productionStepExecutionJson.mergedProductionStepExecutions) {
				const mergedPseJson = merged.productionStepExecution?.toJSON ? merged.productionStepExecution.toJSON() : merged.productionStepExecution
				if (mergedPseJson?.isSectionLastStep && mergedPseJson?.section?.objectId) {
					sectionIdsToFetch.add(mergedPseJson.section.objectId)
				}
			}
			for (const sectionId of sectionIdsToFetch) {
				const sectionWithOutputs = await new Parse.Query(Parse.Object.extend("Section"))
					.equalTo("objectId", sectionId)
					.first()
				if (sectionWithOutputs?.get("sectionOutputs")?.length > 0) {
					sectionsToCheck.push({ objectId: sectionId, sectionOutputs: sectionWithOutputs.get("sectionOutputs") })
				}
			}
		}
		for (const section of sectionsToCheck) {
			//unlock all PSE using this sectionOutputs
			const ulteriorSectionPriorSteps = await getUlteriorSectionPriorSteps(section.sectionOutputs, productionStepExecutionJson.productionDate)
			// set the ulteriorStep as TODO if all PSE on this sectionOutputs are DONE
			for (const ulteriorSectionPriorStep of ulteriorSectionPriorSteps) {
				if (ulteriorSectionPriorStep.get("status") === PSE_STATUS.locked && await isPSECanBeUnlocked(ulteriorSectionPriorStep)) {
					ulteriorSectionPriorStep.set("status", PSE_STATUS.todo)
					await ulteriorSectionPriorStep.save()
				}
			}
		}

		// --- current pse ulteriorStep --- //
		if (newProductionStepExecution.has("ulteriorStep")) {
			const currentUlteriorStep = newProductionStepExecution.get("ulteriorStep")
			const ulteriorStep = await getProductionStepExecution(currentUlteriorStep.id, false, ["priorSteps.step"])

			if (ulteriorStep && ulteriorStep.get("status") === PSE_STATUS.locked && await isPSECanBeUnlocked(ulteriorStep)) {
				ulteriorStep.set("status", PSE_STATUS.todo)
				const updatedUlteriorStep = await ulteriorStep.save()
				productionStepExecutionJson.ulteriorStep = updatedUlteriorStep.toJSON()
			}
		}

		if (temporaryActivePSE.length > 0) {
			temporaryActivePSE.forEach(pse => {
				pse.set("isActive", false)
			})
			await Parse.Object.saveAll(temporaryActivePSE)
		}

		const allPSEComments = productionStepExecutionJson.section ? await getAllPSEWithCommentsBySection(productionStepExecutionJson.section.objectId, productionStepExecutionJson.productionDate) : []

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution: productionStepExecutionJson,
			allPSEComments
		})
	})
}

export const refreshProductionStepExecution = ({ pseId, include }) => {
	return actionWithLoader(async (dispatch) => {
		const productionStepExecution = await getProductionStepExecution(pseId, true, include)

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution
		})
	})
}

export const updateProductionStepExecutionLots = ({ id, secondaryDLCsIds, lotsIds, newSubStatus }) => {
	return actionWithLoader(async (dispatch) => {
		const productionStepExecution = await getProductionStepExecution(id, false)
		const lots = []
		secondaryDLCsIds.forEach(secondaryDLCId => {
			lots.push({
				__type: "Pointer",
				className: COLLECTION_NAMES.secondaryDLC,
				objectId: secondaryDLCId
			})
		})
		lotsIds.forEach(lotId => {
			lots.push({
				__type: "Pointer",
				className: COLLECTION_NAMES.lot,
				objectId: lotId
			})
		})
		productionStepExecution.set("lots", lots)
		if (newSubStatus) {
			productionStepExecution.set("subStatus", newSubStatus)
		}
		await productionStepExecution.save()

		// refresh to allow to include lots
		await refreshProductionStepExecution({ pseId: id })(dispatch)

	})
}

export const updateProductionStepExecutionFromToTestToDone = (id, values) => {
	return actionWithLoader(async (dispatch) => {
		const productionStepExecution = await getProductionStepExecution(id, false, ["section"])
		// --------- current pse --------- //
		if (values.comment) {
			const newComment = {
				comment: values.comment,
				username: Parse.User.current() ? Parse.User.current().get("username") : null,
				creation_dt: dayjs.tz().valueOf()
			}

			/** update comments only for this current PSE **/
			const currentPSEComments = productionStepExecution.get("comments") ? productionStepExecution.get("comments") : []
			currentPSEComments.push(newComment)
			productionStepExecution.set("comments", currentPSEComments)
		}

		productionStepExecution.set("status", PSE_STATUS.done)

		const newProductionStepExecution = await productionStepExecution.save()
		const productionStepExecutionJson = newProductionStepExecution.toJSON()

		const allPSEComments = productionStepExecutionJson.section
			? await getAllPSEWithCommentsBySection(productionStepExecutionJson.section.objectId, productionStepExecutionJson.productionDate)
			: []

		// if last step, unlock ulterior section prior steps depending on section outputs
		if (productionStepExecutionJson.isSectionLastStep && productionStepExecutionJson.section?.objectId) {
			const sectionResult = await getAllPSEWithCommentsBySection(
				productionStepExecutionJson.section.objectId,
				productionStepExecutionJson.productionDate,
				{ withSection: true }
			)
			if (sectionResult?.section?.sectionOutputs?.length > 0) {
				const ulteriorSectionPriorSteps = await getUlteriorSectionPriorSteps(
					sectionResult.section.sectionOutputs,
					productionStepExecutionJson.productionDate
				)
				for (const ulteriorSectionPriorStep of ulteriorSectionPriorSteps) {
					if (await isPSECanBeUnlocked(ulteriorSectionPriorStep)) {
						ulteriorSectionPriorStep.set("status", PSE_STATUS.todo)
						await ulteriorSectionPriorStep.save()
					}
				}
			}
		}

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution: productionStepExecutionJson,
			allPSEComments
		})
	})
}

export const loadPSEMachinesOptions = ({ productionStepExecution, kitchenAreaId }) => {
	return async (dispatch) => {
		const kitchenAreaPointer = new KitchenArea()
		kitchenAreaPointer.id = kitchenAreaId
		const [machines, withoutMachine, machineTypes] = await Promise.all([
			getMachinesByKitchenArea(kitchenAreaPointer),
			getWithoutMachine(),
			getMachineTypes()
		])

		const newMachines = getPSEMachinesWithSameType(productionStepExecution, machineTypes, machines)

		const machinesOptions = newMachines.length > 0
			? uniqBy([...newMachines, withoutMachine], "objectId")
			: [withoutMachine]

		dispatch({
			type: "LOAD_PSE_MACHINES_OPTIONS",
			machinesOptions: machinesOptions
		})
	}
}

export const loadProductionStepsExecutionToPrepare = () => {
	return actionWithLoader(async (dispatch, getState) => {
		const state = getState()
		const productionDay = getProductionStepsProductionDaySelector(state)
		const site = getProductionStepsSiteSelector(state)
		const kitchenArea = getProductionStepsKitchenAreaSelector(state)

		const filters = { productionDay, site, kitchenArea, states: ["TODO", "LOCKED"] }

		const productionStepsExecutions = await getFilteredProductionStepExecutions({
			filters,
			fieldsToInclude: ["recipe", "section", "productionStepSnapshot.machineType"],
			withUnpackingTransformation: false,
		})

		const kitchenAreaPointer = new KitchenArea()
		kitchenAreaPointer.id = kitchenArea.objectId

		const [machines, withoutMachine, machineTypes] = await Promise.all([
			getMachinesByKitchenArea(kitchenAreaPointer),
			getWithoutMachine(),
			getMachineTypes()
		])

		for (const productionStepExecution of productionStepsExecutions) {
			const newMachines = getPSEMachinesWithSameType(productionStepExecution, machineTypes, machines)
			const sortedMachineOptions = newMachines.sort((a, b) => a.name.localeCompare(b.name))
			productionStepExecution.machinesOptions = [...sortedMachineOptions, withoutMachine]
		}

		dispatch({
			type: "PRODUCTION_STEPS_EXECUTIONS_LOADED",
			productionStepsExecutions
		})
	})
}

export const updateProductionStepExecutionSubStatus = ({ id, newSubStatus }) => {
	return actionWithLoader(async (dispatch) => {
		const productionStepExecution = await getProductionStepExecution(id, false)
		productionStepExecution.set("subStatus", newSubStatus)
		const newProductionStepExecution = await productionStepExecution.save()
		const productionStepExecutionJson = newProductionStepExecution.toJSON()

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution: productionStepExecutionJson
		})
	})
}

export const updateProductionStepExecutionsToInProgress = () => {
	return actionWithLoader(async (dispatch, getState) => {
		const productionStepExecution = getProductionStepExecutionSelector(getState())
		const productionStepExecutionObject = await getProductionStepExecution(productionStepExecution.objectId, false, [])

		productionStepExecutionObject.set("status", PSE_STATUS.inProgress)
		if (productionStepExecutionObject.get("subStatus") !== PSE_SUBSTATUSES.weightValidation)
			productionStepExecutionObject.set("subStatus", computeFirstSubStatus(productionStepExecution))
		productionStepExecutionObject.set("startTime", dayjs().utc().valueOf())
		productionStepExecutionObject.set("machinesBatch", productionStepExecution.machinesBatch)
		await productionStepExecutionObject.save()
		const productionStepExecutionJson = productionStepExecutionObject.toJSON()

		let allPSEComments = []
		if (productionStepExecutionJson && productionStepExecutionJson.section) {
			allPSEComments = await getAllPSEWithCommentsBySection(productionStepExecutionJson.section.objectId, productionStepExecutionJson.productionDate)
		}

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution: productionStepExecutionJson,
			allPSEComments
		})
	})
}
// ------------------------- EDITION ------------------------- //
export const saveProductionStepsExecutionsEditedHours = (productionStepsExecutionsValues) => {
	return actionWithLoader(async () => {
		await saveProductionStepsExecutionsWithHours(productionStepsExecutionsValues)
	})
}

export const saveProductionStepsCoolingInformations = ({
	productionStepExecutionId,
	machinesBatch,
	newSubStatus
}) => {
	return actionWithLoader(async (dispatch) => {
		const productionStepExecution = await getProductionStepExecution(productionStepExecutionId, false)
		const formattedBatches = machinesBatch.map(batch => {
			return {
				machine: {
					__type: "Pointer",
					className: "Machines",
					objectId: batch.machineId
				},
				machineName: batch.machineName,
				startTemperature: batch.startTemperature,
				coolingStartTime: editableHourToTimestamp(batch.coolingStartTime, new Date())
			}
		})
		productionStepExecution.set("machinesBatch", formattedBatches)
		if (newSubStatus) {
			productionStepExecution.set("subStatus", newSubStatus)
		}

		const savedProductionStepExecution = await productionStepExecution.save()

		const productionStepExecutionJson = savedProductionStepExecution.toJSON()

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution: productionStepExecutionJson,
		})
	})
}

export const saveProductionStepExecutionEndCoolingInformations = ({
	productionStepExecutionId,
	machinesBatch,
	newSubStatus
}) => {
	return actionWithLoader(async (dispatch) => {
		const productionStepExecution = await getProductionStepExecution(productionStepExecutionId, false)
		const formattedBatches = machinesBatch.map(batch => {
			return {
				machine: {
					__type: "Pointer",
					className: "Machines",
					objectId: batch.machineId
				},
				machineName: batch.machineName,
				startTemperature: batch.startTemperature,
				coolingStartTime: batch.coolingStartTime,
				endTemperature: batch.endTemperature,
				coolingEndTime: editableHourToTimestamp(batch.coolingEndTime, new Date())
			}
		})
		productionStepExecution.set("machinesBatch", formattedBatches)
		if (newSubStatus) {
			productionStepExecution.set("subStatus", newSubStatus)
			if (newSubStatus === PSE_SUBSTATUSES.weightValidation) {
				productionStepExecution.set("status", PSE_STATUS.todo)
			}
		}

		const savedProductionStepExecution = await productionStepExecution.save()

		const productionStepExecutionJson = savedProductionStepExecution.toJSON()

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution: productionStepExecutionJson,
		})
	})
}

export const saveProductionStepExecutionEndCookingInformations = ({
	productionStepExecutionId,
	machinesBatch,
	newSubStatus
}) => {
	return actionWithLoader(async (dispatch) => {
		const productionStepExecution = await getProductionStepExecution(productionStepExecutionId, false)
		const formattedBatches = machinesBatch.map(batch => {
			return {
				machine: {
					__type: "Pointer",
					className: "Machines",
					objectId: batch.machineId
				},
				machineName: batch.machineName,
				endTemperature: batch.endTemperature,
				cookingEndTime: editableHourToTimestamp(batch.cookingEndTime, new Date())
			}
		})
		productionStepExecution.set("machinesBatch", formattedBatches)
		if (newSubStatus) {
			productionStepExecution.set("subStatus", newSubStatus)
		}

		const savedProductionStepExecution = await productionStepExecution.save()

		const productionStepExecutionJson = savedProductionStepExecution.toJSON()

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution: productionStepExecutionJson,
		})
	})
}

export const saveProductionStepExecutionWeightValidationInformation = ({
	productionStepExecutionId,
	netWeight,
	newSubStatus,
	sectionOutputWeights,
	packagingDateWeights,
}) => {
	return actionWithLoader(async (dispatch) => {
		const savedProductionStepExecution = await Parse.Cloud.run("setPSEWeightValidation", {
			id: productionStepExecutionId,
			netWeight,
			subStatus: newSubStatus,
			sectionOutputWeights: sectionOutputWeights,
			packagingDateWeights: packagingDateWeights || undefined,
		},
			// Without explicitly passing sessionToken, `request.user` is undefined in afterSave
			{ sessionToken: Parse.User.current().getSessionToken() }
		)

		const productionStepExecutionJson = savedProductionStepExecution.toJSON()

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution: productionStepExecutionJson,
		})

	})
}

export const saveMergedPSEWeightValidationInformation = ({ productionStepExecutionId, netWeightValues, newSubStatus, sectionOutputWeightsByPSE }) => {
	return actionWithLoader(async (dispatch) => {
		const toBeIncluded = [
			"subStatus",
			"mergedProductionStepExecutions",
			"mergedProductionStepExecutions.productionStepExecution",
			"mergedProductionStepExecutions.productionStepExecution.section",
			"mergedProductionStepExecutions.ulteriorStep",
		]

		const productionStepExecution = await getProductionStepExecution(productionStepExecutionId, false, toBeIncluded)
		productionStepExecution.set("netWeight", netWeightValues.netWeight)
		productionStepExecution.set("isActive", false)

		if (newSubStatus) {
			productionStepExecution.set("subStatus", newSubStatus)
		} else {
			productionStepExecution.set("status", productionStepExecution.get("isSectionLastStep") ? PSE_STATUS.toTest : PSE_STATUS.done)
			productionStepExecution.unset("subStatus")
		}

		for (const individualPSEObj of productionStepExecution.get("mergedProductionStepExecutions")) {
			const individualPSE = individualPSEObj.productionStepExecution
			const netWeightOption = netWeightValues.netWeightMerged.find(value => value.idPSE === individualPSE.id)
			if (netWeightOption) {
				const isInsufficientWeight = getInsufficientWeight(netWeightOption.value, individualPSEObj.theoreticalNetWeight)

				individualPSE.set("netWeight", netWeightOption.value)
				individualPSE.set("startTime", productionStepExecution.get("startTime"))
				individualPSE.set("machinesBatch", productionStepExecution.get("machinesBatch"))
				individualPSE.set("isActive", true)

				let status = productionStepExecution.get("isSectionLastStep") ? PSE_STATUS.toTest : PSE_STATUS.done
				if (isInsufficientWeight) {
					status = PSE_STATUS.inProgress
					individualPSE.set("subStatus", PSE_SUBSTATUSES.insufficientWeight)
				}
				individualPSE.set("status", status)

				const priorStepObjectIds = individualPSE.get("priorSteps")?.map(priorStep => priorStep.step?.id)
				individualPSE.get("priorSteps")?.forEach(priorStep => {
					const stepId = priorStep.step?.id
					if (priorStepObjectIds.includes(stepId)) priorStep.realNetWeight = individualPSE.get("netWeight")
				})

				const ulteriorPSEId = individualPSE.get("ulteriorStep")?.id || individualPSE.get("ulteriorStep")?.objectId
				if (ulteriorPSEId) {
					const ulteriorPSE = await getProductionStepExecution(ulteriorPSEId, false, ["priorSteps.step"])

					if (ulteriorPSE?.has("priorSteps")) {
						const priorSteps = ulteriorPSE.get("priorSteps") || []
						const currentStepId = productionStepExecution.id

						// Update the realNetWeight of the matching prior step
						for (const priorStep of priorSteps) {
							const stepId = priorStep.step?.id || priorStep.step?.objectId
							if (stepId === currentStepId) {
								priorStep.realNetWeight = individualPSE.get("netWeight")
							}
						}

						// Check if all priorSteps now have a realNetWeight
						const allCompleted = priorSteps.every(priorStep => typeof priorStep.realNetWeight === "number" && !isNaN(priorStep.realNetWeight))

						if (allCompleted) {
							ulteriorPSE.set("status", PSE_STATUS.todo)

						}
					}
					await ulteriorPSE.save()
				}

				// Save sectionOutputWeights directly on the individual PSE (clean, same format as classic PSEs)
				const pseObjectId = individualPSE.id || individualPSE.objectId
				const hasProvidedSectionOutputWeights = !!(sectionOutputWeightsByPSE?.[pseObjectId])
				if (hasProvidedSectionOutputWeights) {
					individualPSE.set("sectionOutputWeights", sectionOutputWeightsByPSE[pseObjectId])
				}

				await individualPSE.save(null, { context: { from: PSE_UPDATED_FROM.CLIENT_INDIVIDUAL_STATUS_CHANGE } })

				// if last step, unlock ulterior section prior steps depending on section outputs
				// Also trigger for done status when section outputs were provided during merged step
				if (status === PSE_STATUS.toTest || (status === PSE_STATUS.done && hasProvidedSectionOutputWeights)) {
					const sectionObjectId = individualPSE.get("section")?.id || individualPSE.get("section")?.objectId
					if (sectionObjectId) {
						const productionDate = productionStepExecution.get("stepProductionDate")
						const sectionWithOutputs = await new Parse.Query(Parse.Object.extend("Section"))
							.equalTo("objectId", sectionObjectId)
							.first()
						const sectionOutputs = sectionWithOutputs?.get("sectionOutputs") || []
						if (sectionOutputs.length > 0) {
							const ulteriorSectionPriorSteps = await getUlteriorSectionPriorSteps(
								sectionOutputs,
								productionDate
							)
							for (const ulteriorSectionPriorStep of ulteriorSectionPriorSteps) {
								if (await isPSECanBeUnlocked(ulteriorSectionPriorStep)) {
									ulteriorSectionPriorStep.set("status", PSE_STATUS.todo)
									await ulteriorSectionPriorStep.save()
								}
							}
						}
					}
				}
			}
		}

		const savedProductionStepExecution = await productionStepExecution.save()
		const productionStepExecutionJson = savedProductionStepExecution.toJSON()

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution: productionStepExecutionJson,
		})
	})
}

export const regeneratePSEReproduction = (pseId, reprodId = null, values) => {

	return actionWithLoader(async (dispatch) => {
		const { originalPSE: productionStepExecution } = await Parse.Cloud.run(
			"regeneratePSEAndPriorSteps",
			{
				pseId: pseId,
				netWeight: values.weight,
				stepProductionDate: values.stepProductionDate ?? null
			})

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution: productionStepExecution?.toJSON(),
		})

		// Update the related reproduction need card
		if (reprodId) dispatch(updateReproductionNeed(reprodId, "TODO", values.weight))
	})
}

export const mergeProductionStepExecutions = (productionDate, productionStepIds = [], oldMergedPSEsWithRecipes = null) => {
	return actionWithLoader(async (dispatch) => {
		try {
			const params = { productionDate, productionStepIds }
			if (oldMergedPSEsWithRecipes) {
				params.oldMergedPSEsWithRecipes = oldMergedPSEsWithRecipes
			}
			const result = await Parse.Cloud.run("mergeProductionStepExecutions", params)
			if (!result.success) {
				throw new Error("Une erreur est survenue lors de la fusion des étapes de production.")
			}

			dispatch(openAppMessageSnackBar("Les étapes de production ont été fusionnées avec succès."))
			dispatch({
				type: "TOGGLE_PSE_FUSION_DIALOG",
				open: false
			})
		} catch (error) {
			console.log({ error })
			dispatch(openAppMessageSnackBar(error.message, "error"))
		}
	})
}

export const updateProductionStepExecutionComments = (id, comments) => {
	return actionWithLoader(async (dispatch) => {
		const productionStepExecution = await getProductionStepExecution(id, false)
		// --------- current pse --------- //
		if (comments && comments.length > 0) {
			productionStepExecution.set("comments", comments)
		}

		const newProductionStepExecution = await productionStepExecution.save()
		const productionStepExecutionJson = newProductionStepExecution.toJSON()
		const allPSEComments = await getAllPSEWithCommentsBySection(productionStepExecutionJson.section.objectId, productionStepExecutionJson.productionDate)

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution: productionStepExecutionJson,
			allPSEComments
		})
	})
}

export const substituteSupplierItemsInProductionStepExecutions = (values) => {
	return actionWithLoader(async (dispatch) => {
		const { productionStepExecutions, originalSupplierItem, replacementSupplierItem, recipes, productionDate } = values

		try {
			// 1. Unmerge PSE by recipe and production date
			const { productionStepIds, oldMergedPSEsWithRecipes } = await Parse.Cloud.run("unmergePSEByRecipeAndProductionDate", { productionDate, recipeIds: recipes })
			let pseListToUpdate = await loadPSEByProductionDateAndSupplierItem({
				productionDate,
				supplierItemId: originalSupplierItem
			})

			// 2. Run substitution
			pseListToUpdate = pseListToUpdate.filter(pse => values.recipes.includes(pse.recipe.objectId || pse.recipe.id))
			values.productionStepExecutions = pseListToUpdate.map(pse => pse.objectId)

			await dispatch(saveProductionSubstitution(values, false))

			await Parse.Cloud.run("substituteSupplierItemsInProductionStepExecutions", { productionStepExecutionsIds: productionStepExecutions, originalSupplierItemId: originalSupplierItem, replacementSupplierItemId: replacementSupplierItem })

			// 3. re-merge PSEs with old->new mapping for link repair
			if (productionStepIds.length > 0) {
				await dispatch(mergeProductionStepExecutions(dayjs(values.productionDate).utc().startOf("day").valueOf(), productionStepIds, oldMergedPSEsWithRecipes))
			}

			dispatch(
				openAppMessageSnackBar(`Le remplacement d’article dans ${recipes.length} recettes a bien été effectué pour la journée de production du ${dayjs(productionDate).format("DD/MM/YYYY")}`)
			)
		}
		catch (e) {
			dispatch(showError(e.message))
		}
	})
}

export const updatePSEMergeHistory = (reusableStepIds, stepProductionDate) => {
	return actionWithLoader(async () => {
		const user = Parse.User.current() ? Parse.User.current() : null
		for (const reusableStepId of reusableStepIds) {
			const reusableStep = await getReusableProductionStep(reusableStepId, false)
			await saveMergeHistory(reusableStep, stepProductionDate, user)
		}
	})
}

export const updatePSEMachinesBatchTemperatureForAlerts = (pseId, values) => {
	return actionWithLoader(async (dispatch) => {
		const user = Parse.User.current()
		if (!user) {
			throw new Error("Utilisateur non connecté")
		}
		const pse = await getProductionStepExecution(pseId, false, [])

		if (!pse) return
		const machinesBatch = pse.get("machinesBatch") || []
		const updatedMachinesBatch = []
		machinesBatch.forEach((batch, index) => {
			const currentValues = values[index]
			updatedMachinesBatch.push(currentValues ? { ...batch, ...currentValues } : batch)
		})

		pse.set("machinesBatch", updatedMachinesBatch)
		const updatedPse = await pse.save()

		dispatch({
			type: "LOAD_PRODUCTION_STEP_EXECUTION",
			productionStepExecution: updatedPse.toJSON(),
		})
	})
}

export const addPSECookingWithoutFishParasiteEvent = (pseId) => {
	return actionWithLoader(async () => {
		const user = Parse.User.current() ? Parse.User.current() : null
		const pse = await getProductionStepExecution(pseId, false, [])
		if (!pse) return
		const events = pse.get("events") || []
		events.push({
			user,
			date: dayjs().toDate(),
			change: "withoutFishParasite",
			value: true
		})

		pse.set("events", events)
		await pse.save()
	})
}

/**
 * Extract the previous status/subStatus from events (excluding rollback events)
 * Returns the state before the current one by looking at forward progression events
 * Handles multiple consecutive rollbacks correctly
 */
const getPSEPreviousState = (events, currentStatus, currentSubStatus) => {
	// Filter out rollback events to get only forward progression (keep chronological order)
	const forwardEvents = events.filter(e => !e.isRollback)

	// Find the most recent status change TO the current status (search from end)
	let statusChangeEventIndex = -1
	for (let i = forwardEvents.length - 1; i >= 0; i--) {
		if (forwardEvents[i].change === "status" && forwardEvents[i].value === currentStatus) {
			statusChangeEventIndex = i
			break
		}
	}

	if (statusChangeEventIndex === -1) return { previousStatus: null, previousSubStatus: null }

	const statusChangeEvent = forwardEvents[statusChangeEventIndex]
	let previousSubStatus = null

	if (currentSubStatus) {
		// Find the most recent subStatus change that leaded to the current subStatus
		for (let i = forwardEvents.length - 1; i >= 0; i--) {
			if (forwardEvents[i].change === "subStatus" && forwardEvents[i].value === currentSubStatus) {
				previousSubStatus = forwardEvents[i].oldValue || null
				break
			}
		}
	} else {
		// Find the last subStatus that was set BEFORE the status changed to currentStatus
		for (let i = statusChangeEventIndex - 1; i >= 0; i--) {
			if (forwardEvents[i].change === "subStatus") {
				previousSubStatus = forwardEvents[i].value
				break
			}
		}
	}

	return {
		previousStatus: statusChangeEvent?.oldValue || null,
		previousSubStatus
	}
}

/**
 * Go one step back in PSE workflow
 * Handles all cases: TODO, IN_PROGRESS (all subStatuses), DONE, TO_TEST
 * Cleans up all relevant fields (temperatures, times, weights) appropriately
 */
export const PSEGoOneStepBack = async (pseId) => {
	const currentPSE = await new Parse.Query("ProductionStepExecutions")
		.include([
			"mergedProductionStepExecutions.productionStepExecution",
			"mergedProductionStepExecutions.productionStepExecution.ulteriorStep",
			"ulteriorStep"
		])
		.get(pseId)

	const currentStatus = currentPSE.get("status")
	const currentSubStatus = currentPSE.get("subStatus")
	const events = currentPSE.get("events") || []
	const { previousStatus, previousSubStatus } = getPSEPreviousState(events, currentStatus, currentSubStatus)
	const machinesBatch = currentPSE.get("machinesBatch") || []

	// CASE 1: Rolling back from a subStatus within IN_PROGRESS
	if (currentStatus === PSE_STATUS.inProgress && currentSubStatus) {
		if (!previousSubStatus) {
			// We're at the first subStatus, go back to TODO
			currentPSE.unset("startTime")
			currentPSE.unset("subStatus")
			currentPSE.set("status", PSE_STATUS.todo)
			currentPSE.set("machinesBatch", clearMachineBatchFields(machinesBatch, [
				"startTemperature", "coolingStartTime", "cookingStartTime",
				"endTemperature", "coolingEndTime", "cookingEndTime"
			]))
		} else {
			// Go back to previous subStatus
			currentPSE.set("subStatus", previousSubStatus)

			// Determine what fields to clear based on which subStatus we're leaving
			if (currentSubStatus === "WEIGHT_VALIDATION") {
				currentPSE.unset("netWeight")
				// If going back to TEMPERATURE_VALIDATION, only clear end temps
				if (previousSubStatus === "TEMPERATURE_VALIDATION") {
					currentPSE.set("machinesBatch", clearMachineBatchFields(machinesBatch, [
						"endTemperature", "coolingEndTime", "cookingEndTime"
					]))
				} else {
					// Going back to STEP_COMPLETION or earlier: clear all temps
					currentPSE.set("machinesBatch", clearMachineBatchFields(machinesBatch, [
						"startTemperature", "coolingStartTime", "cookingStartTime",
						"endTemperature", "coolingEndTime", "cookingEndTime"
					]))
				}
			} else if (currentSubStatus === "TEMPERATURE_VALIDATION") {
				// Check if we have end temperatures (indicates we came from WEIGHT_VALIDATION rollback)
				const hasEndTemperatures = machinesBatch.some(batch => batch.endTemperature !== undefined)
				if (hasEndTemperatures) {
					// Clear ALL temps and netWeight (we had gone to WEIGHT_VALIDATION)
					currentPSE.set("machinesBatch", clearMachineBatchFields(machinesBatch, [
						"startTemperature", "coolingStartTime", "cookingStartTime",
						"endTemperature", "coolingEndTime", "cookingEndTime"
					]))
					currentPSE.unset("netWeight")
				} else {
					// Normal: only clear start temps
					currentPSE.set("machinesBatch", clearMachineBatchFields(machinesBatch, [
						"startTemperature", "coolingStartTime", "cookingStartTime"
					]))
				}
			} else if (currentSubStatus === "INSUFFICIENT_WEIGHT") {
				currentPSE.unset("netWeight")
			}
		}
	}
	// CASE 2: Rolling back from DONE or TO_TEST
	else if (currentStatus === PSE_STATUS.done || currentStatus === PSE_STATUS.toTest) {
		currentPSE.unset("endTime")
		currentPSE.set("status", previousStatus || PSE_STATUS.inProgress)

		if (previousSubStatus) {
			currentPSE.set("subStatus", previousSubStatus)

			// Clear netWeight if going back to a substatus before WEIGHT_VALIDATION
			const subStatusOrder = ["STEP_COMPLETION", "TEMPERATURE_VALIDATION", "WEIGHT_VALIDATION", "INSUFFICIENT_WEIGHT"]
			const previousIndex = subStatusOrder.indexOf(previousSubStatus)
			const weightValidationIndex = subStatusOrder.indexOf("WEIGHT_VALIDATION")
			if (previousIndex !== -1 && previousIndex < weightValidationIndex) {
				currentPSE.unset("netWeight")
			}
		}

		// Handle merged PSEs: removed all the fields settled at the end
		const isStepMerged = currentPSE.get("isStepMerged")
		const individualPSEs = currentPSE.get("mergedProductionStepExecutions") || []
		let canGoBack = true

		// Collect all ulterior steps to lock (from main PSE and merged PSEs)
		const ulteriorStepsToLock = []
		const ulteriorStepPointer = currentPSE.get("ulteriorStep")

		if (ulteriorStepPointer) ulteriorStepsToLock.push(ulteriorStepPointer)

		if (isStepMerged && individualPSEs.length > 0) {
			currentPSE.set("isActive", true)

			for (const { productionStepExecution: individualPSE } of mergedPSEs) {
				individualPSE.set("isActive", false)
				individualPSE.set("status", PSE_STATUS.todo)
				individualPSE.unset("subStatus")
				individualPSE.unset("startTime")
				individualPSE.unset("endTime")
				individualPSE.unset("netWeight")
				individualPSE.unset("events")
				individualPSE.set("machinesBatch", clearMachineBatchFields(machinesBatch, [
					"startTemperature", "coolingStartTime", "cookingStartTime",
					"endTemperature", "coolingEndTime", "cookingEndTime"
				]))

				// Check and lock ulterior steps
				for (const ulteriorPSEObject of ulteriorStepsToLock) {
					try {
						const ulteriorPSE = await new Parse.Query("ProductionStepExecutions")
							.include(["mergedProductionStepExecutions.productionStepExecution"])
							.get(ulteriorPSEObject.id || ulteriorPSEObject.objectId)

						if (ulteriorPSE?.get("status") === PSE_STATUS.todo) {
							ulteriorPSE.set("status", PSE_STATUS.locked)
							await ulteriorPSE.save()
						} else if (ulteriorPSE?.get("status") !== PSE_STATUS.locked) {
							canGoBack = false
							break
						}
					} catch (error) {
						console.warn("Could not fetch ulterior step:", error.message)
						canGoBack = false
						break
					}
				}

				if (canGoBack) {
					// Determine the previous status and subStatus from events
					const lastStatusEvent = [...events].reverse().find(e =>
						(e.value === "DONE" || e.value === "TO_TEST") && e.change === "status"
					)
					const previousStatus = lastStatusEvent?.oldValue || PSE_STATUS.inProgress

					// Find the last subStatus that was set before going to DONE
					const lastSubStatusEvent = [...events].reverse().find(e => e.change === "subStatus")
					const previousSubStatus = lastSubStatusEvent?.value || null

					// Clear appropriate fields based on previous subStatus
					let clearedMachinesBatch = machinesBatch
					if (previousSubStatus) {
						switch (previousSubStatus) {
							case "TEMPERATURE_VALIDATION":
								clearedMachinesBatch = clearMachineBatchFields(machinesBatch, ["endTemperature", "coolingEndTime", "cookingEndTime"])
								break
							case "WEIGHT_VALIDATION":
								currentPSE.unset("netWeight")
								break
							case "STEP_COMPLETION":
								clearedMachinesBatch = clearMachineBatchFields(machinesBatch, ["startTemperature", "coolingStartTime", "endTemperature", "coolingEndTime", "cookingEndTime"])
								break
						}
					}

					// Update main PSE
					currentPSE.unset("endTime")
					currentPSE.set("status", previousStatus)
					if (previousSubStatus) currentPSE.set("subStatus", previousSubStatus)
					currentPSE.set("machinesBatch", clearedMachinesBatch)

					// Handle merged PSEs: deactivate and update individual PSEs
					if (isStepMerged && mergedPSEs.length > 0) {
						currentPSE.set("isActive", true)

						for (const { productionStepExecution: mergedPSE } of mergedPSEs) {
							// Deactivate the individual PSE
							mergedPSE.set("isActive", false)

							// Copy status and subStatus from main PSE
							mergedPSE.set("status", previousStatus)
							if (previousSubStatus) mergedPSE.set("subStatus", previousSubStatus)
							else mergedPSE.unset("subStatus")

							// Clear times and weights
							mergedPSE.unset("endTime")
							if (previousSubStatus === "WEIGHT_VALIDATION" || !previousSubStatus) {
								mergedPSE.unset("netWeight")
							}

							// Update machinesBatch
							mergedPSE.set("machinesBatch", clearedMachinesBatch)

							await mergedPSE.save(null, { context: { from: PSE_UPDATED_FROM.CLIENT_MERGED_STATUS_CHANGE } })
						}
						await individualPSE.save(null, { context: { from: PSE_UPDATED_FROM.CLIENT_INDIVIDUAL_STATUS_CHANGE } })
					}
				}
			}

			// Collect all ulterior steps that need to be locked (main PSE + merged PSEs)
			const ulteriorStepsToLock = []
			const directUlteriorStep = currentPSE.get("ulteriorStep")
			if (directUlteriorStep && directUlteriorStep.get("status") === PSE_STATUS.todo) {
				ulteriorStepsToLock.push(directUlteriorStep)
			}

			// Check merged PSEs' ulterior steps
			const mergedPSEs = currentPSE.get("mergedProductionStepExecutions") || []
			for (const { productionStepExecution: mergedPSE } of mergedPSEs) {
				const mergedUlteriorStep = mergedPSE.get("ulteriorStep")
				if (mergedUlteriorStep && mergedUlteriorStep.get("status") === PSE_STATUS.todo) {
					ulteriorStepsToLock.push(mergedUlteriorStep)
				}
			}

			// Lock all collected ulterior steps
			if (ulteriorStepsToLock.length > 0) {
				ulteriorStepsToLock.forEach(step => step.set("status", PSE_STATUS.locked))
				await Parse.Object.saveAll(ulteriorStepsToLock)
			}

			await currentPSE.save(null, { context: { isRollback: true, from: PSE_UPDATED_FROM.CLIENT } })
		}
	}
}

// -------------- ONENTER -------------- //
export const onEnterProductionStepsRead = (store) => {
	return onEnter({
		store,
		actionThunk: loadAllProductionStepExecutionsForListPage
	})
}

export const onEnterProdPlannerView = (store) => {
	return onEnter({
		store,
		actionThunk: loadProdPlannerData
	})
}

export const onEnterUnpackingPSEsBySupplierItems = (store) => {
	return onEnter({
		store,
		actionThunk: loadUnpackingPSEsBySupplierItems
	})
}

export const onEnterProductionStepExecution = (store) => {
	return onEnter({
		store,
		actionThunk: loadProductionStepExecution
	})
}

export const onEnterProductionStepsHoursEdit = (store) => {
	return onEnter({
		store,
		actionThunk: loadProductionStepsExecutionToPrepare,
	})
}

export const onEnterProductionStepsSupervision = (store) => {
	return onEnter({
		store,
		actionThunk: loadProductionStepsSupervisionFilters,
		getReplacingPath: getReplacingPath({ needUser: true }),
		haveAccessRight: haveAccessRight({ readProductionSteps: true })
	})
}

// ---------- REDIRECTION ---------- //
export const showProductionStepsLogin = () => {
	return push(PRODUCTION_STEPS_SUPERVISION_URLS.filter)
}

export const showProductionStepsProdPlannerFilter = () => {
	return push(PRODUCTION_STEPS_SUPERVISION_URLS.prodPlannerFilter)
}

export const showProductionStepsSupervision = (queryParamsObj) => {
	const url = parseQueryParamsToUrl(PRODUCTION_STEPS_SUPERVISION_URLS.read, queryParamsObj)
	return push(url)
}

export const showProductionStepsProdPlanner = () => {
	return push(PRODUCTION_STEPS_SUPERVISION_URLS.prodPlanner)
}

export const showProductionStepsHoursEdit = () => {
	return push(PRODUCTION_STEPS_SUPERVISION_URLS.hoursEdit)
}

export const showProductionStepsPreparation = (id) => {
	return push(PRODUCTION_STEPS_SUPERVISION_URLS.preparation + "/" + id)
}

export const showProductionStepExecution = (id, queryParamsObj) => {
	const url = parseQueryParamsToUrl(PRODUCTION_STEPS_SUPERVISION_URLS.root + "/" + id, queryParamsObj)
	return push(url)
}

/**
 * Save planner view tiles to database
 * Converts planner tile positions to PSE fields and saves them
 * Also clears theoretical times and machines for tiles removed from planner
 */
export const savePlannerTiles = (formattedTiles, productionStepsExecutions, productionDay) => {
	return actionWithLoader(async (dispatch) => {
		try {
			// Split tiles into placed and unassigned
			const originalAssignedTiles = formattedTiles.filter(tile => tile.machine != null && tile.start != null && tile.end != null && !tile.originalId)
			const duplicatedAssignedTiles = formattedTiles.filter(tile => tile.machine != null && tile.start != null && tile.end != null && !!tile.originalId)
			const unassignedTiles = formattedTiles.filter(tile => tile.machine == null || tile.start == null || tile.end == null)

			// Convert placed tiles to PSE updates
			const psesToUpdate = []

			// 1. Handle original assigned tiles
			for (const tile of originalAssignedTiles) {
				// Find the corresponding PSE from Redux state
				const pse = productionStepsExecutions.find(p => p.objectId === tile.objectId)
				if (!pse) {
					console.warn(`PSE not found for tile ${tile.objectId}`)
					continue
				}

				// Convert planner coordinates to database values
				const theoreticalStartTime = convertBlockIndexToTimestamp(tile.start, productionDay)
				const theoreticalEndTime = convertBlockIndexToTimestamp(tile.end, productionDay)

				// Fetch machine to get the name
				const machinePointer = await getMachinesById(tile.machine, [], false)
				const machineName = machinePointer.get("name")

				// Prepare the update object
				const updatedPSE = {
					...pse,
					theoreticalStartTime,
					theoreticalEndTime,
					machinesBatch: [{
						machine: {
							__type: "Pointer",
							className: "Machines",
							objectId: tile.machine
						},
						machineName
					}]
				}

				// Add the object in the updates array
				psesToUpdate.push(updatedPSE)
			}

			// 2. Handle duplicated assigned tiles
			for (const tile of duplicatedAssignedTiles) {
				const updatedPSE = psesToUpdate.find(u => u.objectId === tile.originalId)

				// If the original tile has been unassigned then the duplicate must be the new original
				if (!updatedPSE) {
					// Get the corresponding original tile
					const originalTile = formattedTiles.find(t => t.objectId === tile.originalId)

					// Promote the current duplicated tile to original
					tile.objectId = originalTile.objectId
					delete tile.originalId
					// Demote the removed original tile to duplicate
					originalTile.originalId = originalTile.objectId
					originalTile.objectId = crypto.randomUUID()

					// Convert planner coordinates to database values
					const theoreticalStartTime = convertBlockIndexToTimestamp(tile.start, productionDay)
					const theoreticalEndTime = convertBlockIndexToTimestamp(tile.end, productionDay)

					// Fetch machine to get the name
					const machinePointer = await getMachinesById(tile.machine, [], false)
					const machineName = machinePointer.get("name")

					// Prepare the update object
					const pse = productionStepsExecutions.find(p => p.objectId === tile.objectId)
					const updatedPSE = {
						...pse,
						theoreticalStartTime,
						theoreticalEndTime,
						machinesBatch: [{
							machine: {
								__type: "Pointer",
								className: "Machines",
								objectId: tile.machine
							},
							machineName
						}]
					}

					// Add the object in the updates array
					psesToUpdate.push(updatedPSE)
					continue
				}

				// The original tile is already placed, the duplicated stays a duplicated and is treated as it
				// Check it has the correct theoreticalEndTime
				if (convertBlockIndexToTimestamp(tile.end, productionDay) !== updatedPSE.theoreticalEndTime) {
					throw new Error(`L'étape dupliquée doit finir à la même heure que l'étape ${updatedPSE.productionStepSnapshot.name}`)
				}

				// Compute startTimeOffset from original tile
				const startTimeOffset = convertBlockIndexToTimestamp(tile.start, productionDay) - updatedPSE.theoreticalStartTime

				// Fetch machine to get the name
				const machinePointer = await getMachinesById(tile.machine, [], false)
				const machineName = machinePointer.get("name")

				// Add the machine in the updates array
				updatedPSE.machinesBatch.push({
					machine: {
						__type: "Pointer",
						className: "Machines",
						objectId: tile.machine
					},
					machineName,
					startTimeOffset
				})
			}

			// 3. Handle unassigned tiles
			unassignedTiles.forEach(tile => {
				const assignedPse = psesToUpdate.find(p => p.objectId === tile.originalId || p.objectId === tile.objectId)

				// If the tile is already assigned in the updates
				if (assignedPse) {
					// Check if the machineBatch array of assignedPse contains the machine of the unassigned tile
					const machineBatch = assignedPse.machinesBatch.find(m => m.machine.objectId === tile.machine)

					if (machineBatch) {
						// Remove the machine from the machineBatch
						assignedPse.machinesBatch = assignedPse.machinesBatch.filter(m => m.machine.objectId !== tile.machine)
					}

					return
				}

				// If the tile is not assigned in the updates, the pse may need a clearing
				const pse = productionStepsExecutions.find(p => p.objectId === tile.objectId)
				if (!pse) {
					console.warn(`PSE not found for unassigned tile ${tile.objectId}`)
					return
				}

				if (!pse.theoreticalStartTime && !pse.theoreticalEndTime && (!pse.machinesBatch || pse.machinesBatch.length === 0)) {
					// No need to clear already empty fields
					return
				}

				// Prepare the update object with cleared fields
				const updatedPSE = {
					...pse,
					theoreticalStartTime: null,
					theoreticalEndTime: null,
					machinesBatch: []
				}

				// Add the object in the updates array
				psesToUpdate.push(updatedPSE)
			})

			// Call the save function with all updates
			await dispatch(savePlannerChanges(psesToUpdate, productionStepsExecutions))

			await dispatch(goBack())
		} catch (error) {
			dispatch(openAppMessageSnackBar(error.message, "error"))
		}
	})
}

/**
 * Save planner changes for multiple PSEs (internal use)
 * Updates theoreticalStartTime, theoreticalEndTime, and machinesBatch
 * Deletes fields for unassigned tiles
 */
const savePlannerChanges = (updates, productionStepsExecutions) => {
	return actionWithLoader(async (dispatch) => {
		try {
			// Fetch Parse objects for the PSEs that need updating
			const query = new Parse.Query("ProductionStepExecutions")
			query.containedIn("objectId", updates.map(u => u.objectId))
			const parseObjects = await query.find()

			// Update each Parse object
			parseObjects.forEach(pse => {
				const update = updates.find(u => u.objectId === pse.id)
				if (update) {
					// If values are null, delete the fields; otherwise set them
					if (update.theoreticalStartTime === null) pse.unset("theoreticalStartTime")
					else pse.set("theoreticalStartTime", update.theoreticalStartTime)

					if (update.theoreticalEndTime === null) pse.unset("theoreticalEndTime")
					else pse.set("theoreticalEndTime", update.theoreticalEndTime)

					if (update.machinesBatch && update.machinesBatch.length === 0) pse.set("machinesBatch", [])
					else pse.set("machinesBatch", update.machinesBatch)
				}
			})

			// Save all at once
			await Parse.Object.saveAll(parseObjects)

			// Update Redux state with new values
			const updatedPSEs = productionStepsExecutions.map(pse => {
				const update = updates.find(u => u.objectId === pse.objectId)
				if (update) {
					return {
						...pse,
						theoreticalStartTime: update.theoreticalStartTime,
						theoreticalEndTime: update.theoreticalEndTime,
						machinesBatch: update.machinesBatch
					}
				}
				return pse
			})

			dispatch({
				type: "PRODUCTION_STEPS_EXECUTIONS_LOADED",
				productionStepsExecutions: updatedPSEs
			})

			dispatch(openAppMessageSnackBar(`${parseObjects.length} étape(s) de production enregistrée(s) avec succès`))
		} catch (error) {
			console.error("Error saving planner changes:", error)
			dispatch(openAppMessageSnackBar(error.message, "error"))
			throw error
		}
	})
}